import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import ModuleHeader, { ModuleHeaderAction } from "../../components/ModuleHeader/ModuleHeader";
import ModuleTable from "../../components/ModuleTable/ModuleTable";
import { getAdminSettings, updateAdminSettings } from "../../api/adminSettingApi";
import { listAdminMerchants } from "../../api/adminMerchantApi";
import { exportAdminOrders, getAdminOrderMeta, listAdminOrders } from "../../api/adminOrderApi";
import { exportAdminReport, getAdminReports } from "../../api/adminReportApi";
import "./module.scss";

const merchantStatuses = ["All Status", "Active", "Inactive", "Pending Setup"];

const config = {
  orders: {
    title: "Orders",
    subtitle: "Monitor guest checkout orders, payment status, and collection progress.",
    action: "Export Orders",
    columns: ["Order No", "Merchant / Outlet", "Customer", "Status", "Pickup Window", "Amount"],
    rows: [
      ["SB-10084", "Hikari House / Kuching Central", "Channie / 012-3456789", "Paid", "Today, 6:30 PM", "RM18.90"],
      ["SB-10083", "Green Bowl / Padungan", "Daniel / 011-2233445", "Paid", "Today, 7:00 PM", "RM12.50"],
      ["SB-10082", "Daily Bakery / Miri Times Square", "Mei Ling / 016-9988776", "Collected", "Today, 5:30 PM", "RM9.90"],
      ["SB-10081", "Urban Cafe / Sibu Central", "Aaron / 014-5566778", "Cancelled", "Today, 8:00 PM", "RM15.80"],
    ],
  },

  merchants: {
    title: "Merchants",
    subtitle: "Manage merchant accounts and review performance across all outlets.",
    action: "+ Add Merchant",
    columns: ["Merchant Name", "Merchant Code", "Total Outlets", "Active Outlets", "Overall Status", "Total Orders", "Total Sales", "Actions"],
    rows: [
      {
        summary: ["Hikari House", "HIKARI", "3", "3", "Active", "184", "RM3,628.40", "View"],
        outlets: [
          ["Kuching Central", "Lot 12, Kuching Central", "Chicken Katsu Bento", "Active", "96 orders"],
          ["Vivacity Megamall", "Level 2, Vivacity Megamall", "Teriyaki Rice Box", "Active", "58 orders"],
          ["Samarahan Express", "Uni Central, Kota Samarahan", "Salmon Rice Box", "Active", "30 orders"],
        ],
      },
      {
        summary: ["Green Bowl", "GREEN_BOWL", "2", "2", "Active", "126", "RM2,148.50", "View"],
        outlets: [
          ["Padungan", "Jalan Padungan", "Healthy Bowl", "Active", "72 orders"],
          ["Batu Kawa", "Emart Batu Kawa", "Protein Salad", "Active", "54 orders"],
        ],
      },
      {
        summary: ["Daily Bakery", "DAILY_BAKERY", "1", "0", "Pending Setup", "0", "RM0.00", "View"],
        outlets: [
          ["Miri Times Square", "Ground Floor, Miri Times Square", "Sourdough Set", "Pending Setup", "0 orders"],
        ],
      },
      {
        summary: ["Urban Cafe", "URBAN_CAFE", "1", "0", "Inactive", "88", "RM1,286.30", "View"],
        outlets: [
          ["Sibu Central", "Jalan Wong Nai Siong", "Matcha Latte", "Inactive", "88 orders"],
        ],
      },
    ],
  },

  administrators: {
    title: "Administrators",
    subtitle: "Manage administrator accounts and platform access.",
    action: "+ Add Administrator",
    columns: [
      "Name",
      "Email",
      "Mobile",
      "Status",
      "Last Login",
      "Actions",
    ],
    rows: [
      [
        "John Tan",
        "john@secondbite.my",
        "012-3456789",
        "Active",
        "Today, 9:25 AM",
        "Edit",
      ],
      [
        "Sarah Lim",
        "sarah@secondbite.my",
        "016-9988776",
        "Active",
        "Yesterday, 5:40 PM",
        "Edit",
      ],
      [
        "Daniel Wong",
        "daniel@secondbite.my",
        "011-2233445",
        "Inactive",
        "12 Jun 2026",
        "Edit",
      ],
    ],
  },

  settings: {
    title: "Settings",
    subtitle: "Manage platform configuration, payment gateway, and email notification settings.",
    action: "Save Settings",
    columns: ["Setting", "Description", "Value", "Status", "Actions"],
    rows: [
      ["General", "Platform name, support contact, and maintenance mode.", "SecondBite", "Enabled", "Edit"],
      ["Checkout", "Checkout timeout and important notice. Customer name is compulsory.", "15 minutes", "Enabled", "Edit"],
      ["Payment Gateway", "Fiuu or 2C2P merchant configuration.", "Fiuu", "Enabled", "Edit"],
      ["Email Notification", "SMTP and automated email notification settings.", "Enabled", "Enabled", "Edit"],
      ["Order", "Unpaid order cancellation and pickup grace period.", "15 min auto cancel", "Enabled", "Edit"],
    ],
  },

  reports: {
    title: "Reports",
    subtitle: "View MVP sales and order performance reports.",
    action: "Export Report",
    columns: ["Report", "Description", "Date Range", "Total Orders", "Total Revenue", "Actions"],
    rows: [
      [
        "Sales Report",
        "Revenue summary by selected date range and merchant.",
        "Today",
        "32",
        "RM682.40",
        "View",
      ],
      [
        "Orders Report",
        "Order activity, collection status, cancellations, and refunds.",
        "Today",
        "32",
        "RM682.40",
        "View",
      ],
      [
        "Performance Report",
        "Bag availability and sell-through performance by merchant and outlet.",
        "Today",
        "41 bags",
        "70.7%",
        "View",
      ],
    ],
  },
};

const slugify = (value) => String(value).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const formatFilterDate = (date) => {
  if (!(date instanceof Date)) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ModulePage = ({ type }) => {
  const navigate = useNavigate();
  const page = config[type] || config.orders;

  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });

  const [orderStatus, setOrderStatus] = useState("All Status");
  const [orderMerchantId, setOrderMerchantId] = useState("");
  const [orderOutletId, setOrderOutletId] = useState("");
  const [orderKeyword, setOrderKeyword] = useState("");
  const [orderMerchants, setOrderMerchants] = useState([]);
  const [orderOutlets, setOrderOutlets] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [orderRows, setOrderRows] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(type === "orders");
  const [ordersMessage, setOrdersMessage] = useState("");
  const [merchantStatus, setMerchantStatus] = useState("All Status");
  const [merchantRows, setMerchantRows] = useState([]);
  const [merchantLoading, setMerchantLoading] = useState(type === "merchants");
  const [merchantMessage, setMerchantMessage] = useState("");
  const [keyword, setKeyword] = useState("");
  const [expandedMerchants, setExpandedMerchants] = useState({});
  const [editingSetting, setEditingSetting] = useState(null);
  const [settingGroup, setSettingGroup] = useState("All Settings");
  const [settingsLoading, setSettingsLoading] = useState(type === "settings");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState("");
  const [viewingReport, setViewingReport] = useState(null);
  const [reportType, setReportType] = useState("all");
  const [reportMerchantId, setReportMerchantId] = useState("");
  const [reportOutletId, setReportOutletId] = useState("");
  const [reportRows, setReportRows] = useState([]);
  const [reportDetails, setReportDetails] = useState({});
  const [reportMeta, setReportMeta] = useState({ merchants: [], outlets: [] });
  const [reportsLoading, setReportsLoading] = useState(type === "reports");
  const [reportsMessage, setReportsMessage] = useState("");
  const [settingsForm, setSettingsForm] = useState({
    platformName: "SecondBite",
    supportEmail: "support@secondbite.my",
    supportPhone: "+60 12-3456789",
    platformStatus: "Active",
    maintenanceMessage: "We are temporarily closed for maintenance.",
    checkoutTimeout: "15",
    showImportantNotice: true,
    importantNotice: "Please collect your order within the selected pickup time.",
    paymentGateway: "Fiuu",
    merchantId: "SB_FIUU_DEMO",
    secretKey: "********",
    verifyKey: "********",
    paymentMode: "Sandbox",
    smtpHost: "smtp.gmail.com",
    smtpPort: "587",
    smtpUsername: "noreply@secondbite.my",
    smtpPassword: "********",
    fromName: "SecondBite",
    fromEmail: "noreply@secondbite.my",
    sendOrderConfirmation: true,
    sendWelcomeEmail: true,
    sendPasswordResetEmail: true,
    autoCancelMinutes: "15",
    pickupGracePeriod: "30",
  });

  const updateSetting = (field, value) => {
    setSettingsForm((current) => ({ ...current, [field]: value }));
    setSettingsMessage("");
  };

  useEffect(() => {
    if (type !== "settings") return;

    const loadSettings = async () => {
      setSettingsLoading(true);
      setSettingsMessage("");
      try {
        const result = await getAdminSettings();
        setSettingsForm((current) => ({ ...current, ...result.settings }));
      } catch (error) {
        setSettingsMessage(error.message);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadSettings();
  }, [type]);

  useEffect(() => {
    if (type !== "merchants") return;
    const timer = setTimeout(async () => {
      setMerchantLoading(true);
      setMerchantMessage("");
      try {
        const result = await listAdminMerchants({ status: merchantStatus, keyword });
        setMerchantRows((result.data || []).map((merchant) => ({
          id: merchant.id,
          summary: [
            merchant.name,
            merchant.code || "-",
            String(merchant.total_outlets),
            String(merchant.active_outlets),
            merchant.status === 1 ? "Active" : merchant.status === 2 ? "Pending Setup" : "Inactive",
            String(merchant.total_orders),
            `RM${Number(merchant.total_sales || 0).toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            "View",
          ],
          outlets: (merchant.outlets || []).map((outlet) => ({
            id: outlet.id,
            cells: [
              outlet.name,
              outlet.address || outlet.city || "-",
              outlet.product?.name || "No product yet",
              outlet.status === 1 ? "Active" : "Inactive",
              "-",
            ],
          })),
        })));
      } catch (error) {
        setMerchantMessage(error.message);
      } finally {
        setMerchantLoading(false);
      }
    }, keyword ? 300 : 0);
    return () => clearTimeout(timer);
  }, [type, merchantStatus, keyword]);

  const orderFilters = {
    start_date: formatFilterDate(dateRange.startDate),
    end_date: formatFilterDate(dateRange.endDate),
    merchant_id: orderMerchantId,
    outlet_id: orderOutletId,
    status: orderStatus === "All Status" ? "" : orderStatus,
    keyword: orderKeyword.trim(),
  };

  useEffect(() => {
    if (type !== "orders") return;
    const loadMeta = async () => {
      try {
        const meta = await getAdminOrderMeta();
        setOrderMerchants(meta.merchants || []);
        setOrderOutlets(meta.outlets || []);
        setOrderStatuses(meta.statuses || []);
      } catch (error) {
        setOrdersMessage(error.message);
      }
    };
    loadMeta();
  }, [type]);

  useEffect(() => {
    if (type !== "orders") return;
    const loadOrders = async () => {
      setOrdersLoading(true);
      setOrdersMessage("");
      try {
        const result = await listAdminOrders({
          start_date: formatFilterDate(dateRange.startDate),
          end_date: formatFilterDate(dateRange.endDate),
          merchant_id: orderMerchantId,
          outlet_id: orderOutletId,
          status: orderStatus === "All Status" ? "" : orderStatus,
          keyword: orderKeyword.trim(),
        });
        setOrderRows((result.data || []).map((order) => ({
          id: order.id,
          cells: [
            order.order_number,
            `${order.merchant} / ${order.outlet}`,
            `${order.customer}${order.customer_phone ? ` / ${order.customer_phone}` : ""}`,
            String(order.status).replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
            order.pickup_window || "-",
            `RM${Number(order.amount || 0).toFixed(2)}`,
          ],
        })));
      } catch (error) {
        setOrdersMessage(error.message);
      } finally {
        setOrdersLoading(false);
      }
    };
    const timer = window.setTimeout(loadOrders, orderKeyword.trim() ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [
    type,
    dateRange.startDate,
    dateRange.endDate,
    orderMerchantId,
    orderOutletId,
    orderStatus,
    orderKeyword,
  ]);

  const saveSettings = async (closeEditor = false) => {
    setSettingsSaving(true);
    setSettingsMessage("");
    try {
      const result = await updateAdminSettings(settingsForm);
      setSettingsForm((current) => ({ ...current, ...result.settings }));
      setSettingsMessage("Settings saved successfully.");
      if (closeEditor) setEditingSetting(null);
    } catch (error) {
      setSettingsMessage(error.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const fallbackReportDetails = {
    "Sales Report": {
      title: "Sales Report",
      subtitle: "Sales, commission, refunds, and merchant payout by outlet.",
      cards: [
        ["Gross Sales", "RM682.40"],
        ["Refund Amount", "RM15.80"],
        ["Commission", "RM52.00"],
        ["Net Merchant Payout", "RM614.60"],
        ["Total Orders", "32"],
        ["Total Bags Sold", "41"],
      ],
      sections: [
        {
          title: "Sales by Merchant and Outlet",
          columns: ["Merchant", "Outlet", "Total Orders", "Total Bags Sold", "Gross Sales", "Refund Amount", "Commission", "Net Merchant Payout"],
          rows: [
            ["Hikari House", "Kuching Central", "8", "10", "RM150.80", "RM0.00", "RM11.50", "RM139.30"],
            ["Hikari House", "Vivacity Megamall", "4", "5", "RM76.00", "RM0.00", "RM6.00", "RM70.00"],
            ["Green Bowl", "Padungan", "8", "9", "RM100.00", "RM0.00", "RM8.00", "RM92.00"],
            ["Daily Bakery", "Miri Times Square", "7", "8", "RM69.30", "RM0.00", "RM5.50", "RM63.80"],
            ["Urban Cafe", "Sibu Central", "5", "9", "RM286.30", "RM15.80", "RM21.00", "RM249.50"],
          ],
        },
      ],
    },
    "Orders Report": {
      title: "Orders Report",
      subtitle: "Order activity, collection status, cancellations, and refunds.",
      cards: [
        ["Total Orders", "32"],
        ["Collected", "21"],
        ["Paid", "7"],
        ["Cancelled", "2"],
        ["Refunded", "2"],
      ],
      sections: [
        {
          title: "Order Status Breakdown",
          columns: ["Status", "Orders", "Revenue", "Notes"],
          rows: [
            ["Paid", "4", "RM82.40", "Waiting for collection time"],
            ["Paid", "7", "RM148.30", "Waiting for pickup"],
            ["Collected", "21", "RM451.70", "Completed"],
            ["Cancelled", "2", "RM0.00", "Quantity restored"],
            ["Refunded", "2", "RM0.00", "Refund completed"],
          ],
        },
        {
          title: "Recent Orders",
          columns: ["Order No", "Merchant", "Customer", "Status", "Amount"],
          rows: [
            ["SB-10084", "Hikari House", "Channie", "Paid", "RM18.90"],
            ["SB-10083", "Green Bowl", "Daniel", "Paid", "RM12.50"],
            ["SB-10082", "Daily Bakery", "Mei Ling", "Collected", "RM9.90"],
            ["SB-10081", "Urban Cafe", "Aaron", "Cancelled", "RM15.80"],
          ],
        },
      ],
    },
    "Performance Report": {
      title: "Performance Report",
      subtitle: "Bag availability and sell-through performance by merchant and outlet.",
      cards: [
        ["Total Bags Listed", "58"],
        ["Total Bags Sold", "41"],
        ["Unsold Bags", "17"],
        ["Sell-through Rate (%)", "70.7%"],
      ],
      sections: [
        {
          title: "Performance by Merchant and Outlet",
          columns: ["Merchant", "Outlet", "Bags Listed", "Bags Sold", "Unsold Bags", "Sell-through Rate (%)"],
          rows: [
            ["Hikari House", "Kuching Central", "15", "10", "5", "66.7%"],
            ["Hikari House", "Vivacity Megamall", "10", "5", "5", "50.0%"],
            ["Green Bowl", "Padungan", "12", "9", "3", "75.0%"],
            ["Daily Bakery", "Miri Times Square", "9", "8", "1", "88.9%"],
            ["Urban Cafe", "Sibu Central", "12", "9", "3", "75.0%"],
          ],
        },
      ],
    },
  };

  const reportFilters = {
    start_date: formatFilterDate(dateRange.startDate),
    end_date: formatFilterDate(dateRange.endDate),
    merchant_id: reportMerchantId,
    outlet_id: reportOutletId,
    report_type: reportType,
  };

  useEffect(() => {
    if (type !== "reports") return;
    const loadReports = async () => {
      setReportsLoading(true);
      setReportsMessage("");
      try {
        const result = await getAdminReports({
          start_date: formatFilterDate(dateRange.startDate),
          end_date: formatFilterDate(dateRange.endDate),
          merchant_id: reportMerchantId,
          outlet_id: reportOutletId,
          report_type: reportType,
        });
        setReportMeta(result.meta || { merchants: [], outlets: [] });
        setReportDetails(result.details || {});
        setReportRows((result.reports || [])
          .filter((report) => reportType === "all" || report.key === reportType));
      } catch (requestError) {
        setReportsMessage(requestError.message);
      } finally {
        setReportsLoading(false);
      }
    };
    loadReports();
  }, [
    type,
    dateRange.startDate,
    dateRange.endDate,
    reportMerchantId,
    reportOutletId,
    reportType,
  ]);

  const handleAction = () => {
    switch (type) {
      case "merchants":
        navigate("/merchants/create");
        break;

      case "administrators":
        navigate("/administrators/create");
        break;

      case "orders":
        setOrdersMessage("");
        exportAdminOrders(orderFilters).catch((error) => setOrdersMessage(error.message));
        break;

      case "reports":
        setReportsMessage("");
        exportAdminReport(reportFilters).catch((error) => setReportsMessage(error.message));
        break;

      case "settings":
        saveSettings();
        break;
        
      default:
        break;
    }
  };

  const toggleMerchant = (merchantKey) => {
    setExpandedMerchants((current) => ({
      ...current,
      [merchantKey]: !current[merchantKey],
    }));
  };

  const renderFilters = () => {
    if (type === "merchants") {
      return (
        <section className="module-page__filters module-page__filters--merchants">
          <label>
            <span>Merchant Status</span>
            <select value={merchantStatus} onChange={(event) => setMerchantStatus(event.target.value)}>
              {merchantStatuses.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Keyword</span>
            <input
              type="text"
              placeholder="Search merchant name..."
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
            />
          </label>
        </section>
      );
    }

    if (type === "administrators") {
      return (
        <section className="module-page__filters">
          <label>
            <span>Status</span>
            <select>
              <option>All Status</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </label>

          <label>
            <span>Keyword</span>
            <input type="text" placeholder="Search name or email..." />
          </label>

          <div />
        </section>
      );
    }

    if (type === "settings") {
      return (
        <section className="module-page__filters">
          <label>
            <span>Setting Group</span>
            <select value={settingGroup} onChange={(event) => setSettingGroup(event.target.value)}>
              <option>All Settings</option>
              <option>General</option>
              <option>Payment Gateway</option>
              <option>Email Notification</option>
              <option>Checkout</option>
              <option>Order</option>
            </select>
          </label>

          <label>
            <span>Keyword</span>
            <input type="text" placeholder="Search setting..." value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </label>

          <div />
        </section>
      );
    }

    if (type === "reports") {
      return (
        <section className="module-page__filters module-page__filters--orders module-page__filters--orders-with-outlet">
          <label>
            <span>Date Range</span>
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </label>

          <label>
            <span>Merchant</span>
            <select value={reportMerchantId} onChange={(event) => { setReportMerchantId(event.target.value); setReportOutletId(""); }}>
              <option value="">All Merchants</option>
              {(reportMeta.merchants || []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Outlet</span>
            <select value={reportOutletId} onChange={(event) => setReportOutletId(event.target.value)}>
              <option value="">All Outlets</option>
              {(reportMeta.outlets || []).filter((item) => !reportMerchantId || String(item.merchant_id) === String(reportMerchantId)).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Report Type</span>
            <select value={reportType} onChange={(event) => setReportType(event.target.value)}>
              <option value="all">All Reports</option>
              <option value="sales">Sales Report</option>
              <option value="orders">Orders Report</option>
              <option value="performance">Performance Report</option>
            </select>
          </label>
        </section>
      );
    }

    return (
      <section className="module-page__filters module-page__filters--orders module-page__filters--orders-with-outlet">
        <label>
          <span>Date Range</span>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </label>

        <label>
          <span>Merchant</span>
          <select value={orderMerchantId} onChange={(event) => { setOrderMerchantId(event.target.value); setOrderOutletId(""); }}>
            <option value="">All Merchants</option>
            {orderMerchants.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Outlet</span>
          <select value={orderOutletId} onChange={(event) => setOrderOutletId(event.target.value)}>
            <option value="">All Outlets</option>
            {orderOutlets.filter((item) => !orderMerchantId || String(item.merchant_id) === String(orderMerchantId)).map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Order Status</span>
          <select value={orderStatus} onChange={(event) => setOrderStatus(event.target.value)}>
            <option>All Status</option>
            {orderStatuses.map((item) => (
              <option key={item} value={item}>
                {String(item).replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase())}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Keyword</span>
          <input
            type="search"
            value={orderKeyword}
            onChange={(event) => setOrderKeyword(event.target.value)}
            placeholder="Search order or customer..."
          />
        </label>
      </section>
    );
  };

  const renderSwitch = (label, field) => (
    <label className="settings-modal__switch">
      <input
        type="checkbox"
        checked={Boolean(settingsForm[field])}
        onChange={(event) => updateSetting(field, event.target.checked)}
      />
      <span>{label}</span>
    </label>
  );

  const renderSettingsEditor = () => {
    if (!editingSetting) return null;

    const titleMap = {
      General: "Edit General Settings",
      Checkout: "Edit Checkout Settings",
      "Payment Gateway": "Edit Payment Gateway",
      "Email Notification": "Edit Email Notification",
      Order: "Edit Order Settings",
    };

    return (
      <div className="settings-modal" role="presentation">
        <div className="settings-modal__backdrop" onClick={() => setEditingSetting(null)} />
        <section className="settings-modal__dialog" role="dialog" aria-modal="true">
          <div className="settings-modal__header">
            <div>
              <h2>{titleMap[editingSetting] || "Edit Setting"}</h2>
              <p>Update the live platform configuration.</p>
            </div>
            <button type="button" className="settings-modal__close" onClick={() => setEditingSetting(null)}>
              ×
            </button>
          </div>

          <div className="settings-modal__body">
            {editingSetting === "General" && (
              <div className="settings-modal__grid">
                <label>
                  <span>Platform Name</span>
                  <input value={settingsForm.platformName} onChange={(event) => updateSetting("platformName", event.target.value)} />
                </label>
                <label>
                  <span>Support Email</span>
                  <input value={settingsForm.supportEmail} onChange={(event) => updateSetting("supportEmail", event.target.value)} />
                </label>
                <label>
                  <span>Support Phone</span>
                  <input value={settingsForm.supportPhone} onChange={(event) => updateSetting("supportPhone", event.target.value)} />
                </label>
                <label>
                  <span>Platform Status</span>
                  <select value={settingsForm.platformStatus} onChange={(event) => updateSetting("platformStatus", event.target.value)}>
                    <option>Active</option>
                    <option>Maintenance</option>
                  </select>
                </label>
                <label className="settings-modal__wide">
                  <span>Maintenance Message</span>
                  <textarea value={settingsForm.maintenanceMessage} onChange={(event) => updateSetting("maintenanceMessage", event.target.value)} />
                </label>
              </div>
            )}

            {editingSetting === "Checkout" && (
              <div className="settings-modal__grid settings-modal__grid--checkout">
                <label className="settings-modal__wide">
                  <span>Checkout Timeout (minutes)</span>
                  <input type="number" value={settingsForm.checkoutTimeout} onChange={(event) => updateSetting("checkoutTimeout", event.target.value)} />
                </label>

                <div className="settings-modal__stack settings-modal__wide settings-modal__stack--left settings-modal__stack--notice-toggle">
                  {renderSwitch("Show Important Notice", "showImportantNotice")}
                </div>

                <label className="settings-modal__wide">
                  <span>Important Notice</span>
                  <textarea value={settingsForm.importantNotice} onChange={(event) => updateSetting("importantNotice", event.target.value)} />
                </label>
              </div>
            )}

            {editingSetting === "Payment Gateway" && (
              <div className="settings-modal__grid">
                <label>
                  <span>Payment Gateway</span>
                  <select value={settingsForm.paymentGateway} onChange={(event) => updateSetting("paymentGateway", event.target.value)}>
                    <option>Fiuu</option>
                  </select>
                </label>

                <label>
                  <span>Merchant ID</span>
                  <input value={settingsForm.merchantId} onChange={(event) => updateSetting("merchantId", event.target.value)} />
                </label>

                <label>
                  <span>Secret Key</span>
                  <input
                    type="password"
                    value={settingsForm.secretKey}
                    onChange={(event) => updateSetting("secretKey", event.target.value)}
                  />
                </label>

                <label>
                  <span>Verify Key</span>
                  <input
                    type="password"
                    value={settingsForm.verifyKey}
                    onChange={(event) => updateSetting("verifyKey", event.target.value)}
                  />
                </label>

                <label className="settings-modal__wide">
                  <span>Mode</span>
                  <select value={settingsForm.paymentMode} onChange={(event) => updateSetting("paymentMode", event.target.value)}>
                    <option>Sandbox</option>
                    <option>Production</option>
                  </select>
                </label>
              </div>
            )}

            {editingSetting === "Email Notification" && (
              <div className="settings-modal__grid">
                <label>
                  <span>SMTP Host</span>
                  <input value={settingsForm.smtpHost} onChange={(event) => updateSetting("smtpHost", event.target.value)} />
                </label>
                <label>
                  <span>SMTP Port</span>
                  <input value={settingsForm.smtpPort} onChange={(event) => updateSetting("smtpPort", event.target.value)} />
                </label>
                <label>
                  <span>SMTP Username</span>
                  <input value={settingsForm.smtpUsername} onChange={(event) => updateSetting("smtpUsername", event.target.value)} />
                </label>
                <label>
                  <span>SMTP Password</span>
                  <input type="password" value={settingsForm.smtpPassword} onChange={(event) => updateSetting("smtpPassword", event.target.value)} />
                </label>
                <label>
                  <span>From Name</span>
                  <input value={settingsForm.fromName} onChange={(event) => updateSetting("fromName", event.target.value)} />
                </label>
                <label>
                  <span>From Email</span>
                  <input value={settingsForm.fromEmail} onChange={(event) => updateSetting("fromEmail", event.target.value)} />
                </label>
                <div className="settings-modal__stack settings-modal__wide settings-modal__stack--left">
                  {renderSwitch("Send Order Confirmation", "sendOrderConfirmation")}
                  {renderSwitch("Send Welcome Email", "sendWelcomeEmail")}
                  {renderSwitch("Send Password Reset Email", "sendPasswordResetEmail")}
                  <button type="button" className="settings-modal__secondary">Test SMTP</button>
                </div>
              </div>
            )}

            {editingSetting === "Order" && (
              <div className="settings-modal__grid settings-modal__grid--order">
                <label>
                  <span>Auto Cancel Unpaid Order (minutes)</span>
                  <input type="number" value={settingsForm.autoCancelMinutes} onChange={(event) => updateSetting("autoCancelMinutes", event.target.value)} />
                </label>
                <label>
                  <span>Pickup Grace Period (minutes)</span>
                  <input type="number" value={settingsForm.pickupGracePeriod} onChange={(event) => updateSetting("pickupGracePeriod", event.target.value)} />
                </label>
              </div>
            )}
          </div>

          <div className="settings-modal__footer">
            <button type="button" className="settings-modal__secondary" onClick={() => setEditingSetting(null)}>
              Cancel
            </button>
            <button type="button" className="settings-modal__primary" disabled={settingsSaving} onClick={() => saveSettings(true)}>
              {settingsSaving ? "Saving..." : "Save Setting"}
            </button>
          </div>
        </section>
      </div>
    );
  };


  const renderReportViewer = () => {
    if (!viewingReport) return null;

    const report = reportDetails[viewingReport] || fallbackReportDetails["Sales Report"];

    return (
      <div className="settings-modal report-modal" role="presentation">
        <div className="settings-modal__backdrop" onClick={() => setViewingReport(null)} />
        <section className="settings-modal__dialog report-modal__dialog" role="dialog" aria-modal="true">
          <div className="settings-modal__header">
            <div>
              <h2>{report.title}</h2>
              <p>{report.subtitle}</p>
            </div>
            <button type="button" className="settings-modal__close" onClick={() => setViewingReport(null)}>
              ×
            </button>
          </div>

          <div className="settings-modal__body report-modal__body">
            <div className="report-modal__meta">
              <span>Date Range: {reportMeta.date_range || "-"}</span>
              <span>Merchant: {(reportMeta.merchants || []).find((item) => String(item.id) === String(reportMerchantId))?.name || "All Merchants"}</span>
              <span>Outlet: {(reportMeta.outlets || []).find((item) => String(item.id) === String(reportOutletId))?.name || "All Outlets"}</span>
              <span>Generated: {reportMeta.generated_at ? new Date(reportMeta.generated_at).toLocaleString("en-MY") : "-"}</span>
            </div>

            <div className="report-modal__cards">
              {report.cards.map(([label, value]) => (
                <article key={label} className="report-modal__card">
                  <span>{label}</span>
                  <strong>{value}</strong>
                </article>
              ))}
            </div>

            {report.sections.map((section) => (
              <section key={section.title} className="report-modal__section">
                <h3>{section.title}</h3>
                <div className="report-modal__table">
                  <div
                    className="report-modal__table-head"
                    style={{ gridTemplateColumns: `repeat(${section.columns.length}, minmax(120px, 1fr))` }}
                  >
                    {section.columns.map((column) => (
                      <span key={column}>{column}</span>
                    ))}
                  </div>
                  {!(section.rows || []).length ? (
                    <div className="report-modal__empty">No Data Available</div>
                  ) : (
                    section.rows.map((row, rowIndex) => (
                      <div
                        key={`${section.title}-${rowIndex}`}
                        className="report-modal__table-row"
                        style={{ gridTemplateColumns: `repeat(${section.columns.length}, minmax(120px, 1fr))` }}
                      >
                        {row.map((cell, cellIndex) => (
                          <span key={`${section.title}-${rowIndex}-${cellIndex}`}>{cell}</span>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              </section>
            ))}
          </div>

          <div className="settings-modal__footer">
            <button type="button" className="settings-modal__secondary" onClick={() => setViewingReport(null)}>
              Close
            </button>
            <button type="button" className="settings-modal__primary" onClick={() => exportAdminReport({ ...reportFilters, report_type: viewingReport }).catch((error) => setReportsMessage(error.message))}>
              Export Report
            </button>
          </div>
        </section>
      </div>
    );
  };

  const settingRows = [
    ["General", "Platform name, support contact, and maintenance mode.", settingsForm.platformName, "Enabled", "Edit"],
    ["Checkout", "Checkout timeout and important notice. Customer name is compulsory.", `${settingsForm.checkoutTimeout} minutes`, "Enabled", "Edit"],
    ["Payment Gateway", "Fiuu merchant configuration.", settingsForm.paymentGateway, "Enabled", "Edit"],
    ["Email Notification", "SMTP and automated email notification settings.", settingsForm.sendOrderConfirmation ? "Enabled" : "Disabled", "Enabled", "Edit"],
    ["Order", "Unpaid order cancellation and pickup grace period.", `${settingsForm.autoCancelMinutes} min auto cancel`, "Enabled", "Edit"],
  ];
  const normalizedSettingKeyword = keyword.trim().toLowerCase();
  const displayedRows = type === "merchants"
    ? merchantRows
    : type === "orders"
    ? orderRows
    : type === "settings"
    ? settingRows.filter((row) => {
        const matchesGroup = settingGroup === "All Settings" || row[0] === settingGroup;
        const matchesKeyword = !normalizedSettingKeyword
          || row.slice(0, 3).some((value) => String(value).toLowerCase().includes(normalizedSettingKeyword));
        return matchesGroup && matchesKeyword;
      })
    : type === "reports"
    ? reportRows
    : page.rows;

  return (
    <div className="module-page">
      <ModuleHeader
        title={page.title}
        description={page.subtitle}
        actions={(
          <ModuleHeaderAction type="button" onClick={handleAction} disabled={settingsLoading || settingsSaving}>
            {type === "settings" && settingsSaving ? "Saving..." : page.action}
          </ModuleHeaderAction>
        )}
      />

      {renderFilters()}
      {type === "merchants" && (merchantMessage || merchantLoading) && (
        <p className="module-page__feedback">{merchantMessage || "Loading merchants..."}</p>
      )}
      {type === "settings" && settingsMessage && (
        <p className="module-page__feedback">{settingsMessage}</p>
      )}
      {type === "orders" && (ordersMessage || ordersLoading) && (
        <p className="module-page__feedback">{ordersMessage || "Loading orders..."}</p>
      )}
      {type === "reports" && (reportsMessage || reportsLoading) && (
        <p className="module-page__feedback">{reportsMessage || "Loading reports..."}</p>
      )}

      <ModuleTable
        title={`${page.title} Listing`}
        columns={page.columns}
        columnTemplate={
          type === "merchants"
            ? undefined
            : `repeat(${page.columns.length}, minmax(140px, 1fr))`
        }
        variant={type}
        empty={
          ((type === "orders" && !ordersLoading && !ordersMessage && !displayedRows.length)
            || (type === "merchants" && !merchantLoading && !merchantMessage && !displayedRows.length))
            ? "No Data Available"
            : null
        }
      >
        {displayedRows.map((row, index) => {
          const displayRow = type === "merchants"
            ? row.summary
            : type === "orders" || type === "reports"
              ? row.cells
              : row;
          const rowKey = `${displayRow[0]}-${index}`;
          const columnTemplate = `repeat(${page.columns.length}, minmax(140px, 1fr))`;

          if (type === "merchants") {
            return (
              <ModuleTable.MerchantGroup key={rowKey}>
                <ModuleTable.MerchantRow
                  open={Boolean(expandedMerchants[rowKey])}
                  onClick={() => toggleMerchant(rowKey)}
                >
                  {displayRow.map((cell, cellIndex) => {
                    const isBadge = cellIndex === 3 || cellIndex === 4;

                    if (isBadge) {
                      return (
                        <ModuleTable.Status key={`${cell}-${cellIndex}`} value={cell}>
                          {cell}
                        </ModuleTable.Status>
                      );
                    }

                    return (
                      <span key={`${cell}-${cellIndex}`} className="module-table__merchant-cell">
                        <small>{page.columns[cellIndex]}</small>
                        <strong>
                          {cellIndex === 0 && (
                            <span className="module-table__expand-icon" aria-hidden="true">
                              {expandedMerchants[rowKey] ? "⌄" : "›"}
                            </span>
                          )}
                          {cellIndex === 7 ? (
                            <span className="module-table__merchant-actions">
                              <ModuleTable.Action
                                type="text"
                                stopPropagation
                                onClick={() => navigate(`/merchants/${row.id}`)}
                              >
                                {cell}
                              </ModuleTable.Action>
                              <ModuleTable.Action
                                type="text"
                                stopPropagation
                                onClick={() => navigate(`/merchants/${row.id}/outlets/create`)}
                              >
                                Add Outlet
                              </ModuleTable.Action>
                            </span>
                          ) : cell}
                        </strong>
                      </span>
                    );
                  })}
                </ModuleTable.MerchantRow>

                {expandedMerchants[rowKey] && (
                  <ModuleTable.OutletList>
                    {row.outlets.length ? row.outlets.map((outlet, outletIndex) => (
                      <ModuleTable.OutletRow
                        key={`${displayRow[0]}-${outlet.id}`}
                        onClick={() => navigate(`/merchants/${row.id}/outlets/${outlet.id}`)}
                      >
                        <span className="module-table__outlet-index">Outlet {outletIndex + 1}</span>
                        <span>
                          <strong>{outlet.cells[0]}</strong>
                          <small>{outlet.cells[1]}</small>
                        </span>
                        <span>
                          <small>Product</small>
                          <strong>{outlet.cells[2]}</strong>
                        </span>
                        <ModuleTable.Status value={outlet.cells[3]}>{outlet.cells[3]}</ModuleTable.Status>
                        <span>
                          <small>Today</small>
                          <strong>{outlet.cells[4]}</strong>
                        </span>
                      </ModuleTable.OutletRow>
                    )) : (
                      <div className="module-table__outlet-empty">No Outlets Available</div>
                    )}
                  </ModuleTable.OutletList>
                )}
              </ModuleTable.MerchantGroup>
            );
          }

          return (
            <ModuleTable.Row
              key={rowKey}
              clickable={type === "orders" || type === "administrators" || type === "settings" || type === "reports"}
              columnTemplate={columnTemplate}
              onClick={() => {
                if (type === "orders") navigate(`/orders/${row.id}`);
                if (type === "administrators") navigate(`/administrators/${slugify(displayRow[0])}/edit`);
                if (type === "settings") setEditingSetting(displayRow[0]);
                if (type === "reports") setViewingReport(row.key);
              }}
            >
              {displayRow.map((cell, cellIndex) => {
                const isStatus = (cellIndex === 3 && type !== "orders") || cellIndex === 4;
                if (isStatus) {
                  return (
                    <ModuleTable.Status key={`${cell}-${cellIndex}`} value={cell}>
                      {cell}
                    </ModuleTable.Status>
                  );
                }
                if (cell === "Edit") {
                  return (
                    <ModuleTable.Action key={`${cell}-${cellIndex}`} type="text">
                      {cell}
                    </ModuleTable.Action>
                  );
                }
                return <span key={`${cell}-${cellIndex}`}>{cell}</span>;
              })}
            </ModuleTable.Row>
          );
        })}
      </ModuleTable>

      {renderSettingsEditor()}
      {renderReportViewer()}
    </div>
  );
};

export default ModulePage;
